import {
    ADDRESS_ENTRY_STEP,
    ENTER_CODE_STEP,
    GIFT_DETAILS_STEP,
    LOGIN_STEP,
    OPEN_GIFT_STEP,
    REDEMPTION_SELECT_STEP,
    SELECT_BRAND_STEP,
    SUCCESS_STEP,
} from '@/src/modules/pg-plus/redemption/constants'
import {
    GET_PG_PLUS_STEPS,
    GET_VIRTUAL_REDEMPTION_STEPS,
} from '@/src/modules/build-a-card/constants'

export type TStep = {
    stepName: string
    stepText: string
}

type TGetStepsPayload = {
    isLoggedIn: boolean
    currentStep: string
    isPlastic?: boolean
    isBrandSelect?: boolean
    isRedeemed?: boolean
    giftDetailsCanBeViewed?: boolean
    isUnbrandedToBranded?: boolean
}

function getCurrentStepNumber(
    currentStep: string,
    totalSteps: number,
    isLoggedIn: boolean,
    type: typeof GET_VIRTUAL_REDEMPTION_STEPS | typeof GET_PG_PLUS_STEPS
) {
    switch (currentStep) {
        case OPEN_GIFT_STEP:
            return 1
        case ENTER_CODE_STEP:
            return 2
        case LOGIN_STEP:
            return type === GET_VIRTUAL_REDEMPTION_STEPS ? 3 : 2
        case REDEMPTION_SELECT_STEP:
            return isLoggedIn ? 2 : 3
        case ADDRESS_ENTRY_STEP:
            return 3
        case SELECT_BRAND_STEP:
            return 3
        case SUCCESS_STEP:
            return totalSteps
        case GIFT_DETAILS_STEP:
            return 4
        default:
            return 0
    }
}

export function getSteps(
    type: typeof GET_VIRTUAL_REDEMPTION_STEPS | typeof GET_PG_PLUS_STEPS,
    payload: TGetStepsPayload
) {
    const emptyStep: TStep = {
        stepName: '',
        stepText: '',
    }

    const { isLoggedIn, currentStep } = payload
    const loginOrEmptyStep: TStep = !isLoggedIn
        ? {
              stepName: LOGIN_STEP,
              stepText: 'Login/Signup',
          }
        : emptyStep

    switch (type) {
        case GET_VIRTUAL_REDEMPTION_STEPS: {
            const virtualRedemptionSteps: TStep[] = [
                {
                    stepName: OPEN_GIFT_STEP,
                    stepText: 'Open',
                },
                !isLoggedIn
                    ? {
                          stepName: LOGIN_STEP,
                          stepText: 'Login/Signup',
                      }
                    : {
                          stepName: ENTER_CODE_STEP,
                          stepText: 'Enter Code',
                      },
                {
                    stepName: SUCCESS_STEP,
                    stepText: 'View Gift',
                },
            ].filter((step) => !!step.stepName)

            const totalVirtualSteps = virtualRedemptionSteps.length

            return {
                currentStepNumber: getCurrentStepNumber(
                    currentStep,
                    totalVirtualSteps,
                    isLoggedIn,
                    type
                ),
                finalizedSteps: virtualRedemptionSteps,
            }
        }
        case GET_PG_PLUS_STEPS: {
            const {
                isPlastic,
                isBrandSelect,
                isRedeemed,
                giftDetailsCanBeViewed,
                isUnbrandedToBranded,
            } = payload

            const loginOrSelectStep = !isLoggedIn
                ? {
                      stepName: LOGIN_STEP,
                      stepText: 'Login/Signup',
                  }
                : {
                      stepName: REDEMPTION_SELECT_STEP,
                      stepText: 'Choose Gift',
                  }

            // set added step for the step header
            let addedStep = emptyStep

            if (isPlastic) {
                addedStep = {
                    stepName: ADDRESS_ENTRY_STEP,
                    stepText: 'Delivery',
                }
            }

            if (isBrandSelect || isUnbrandedToBranded) {
                addedStep = {
                    stepName: SELECT_BRAND_STEP,
                    stepText: 'Brand',
                }
            }

            const unredeemedSteps = [
                {
                    stepName: OPEN_GIFT_STEP,
                    stepText: 'Open',
                },
                loginOrSelectStep,
                addedStep,
                {
                    stepName: SUCCESS_STEP,
                    stepText: 'Enjoy!',
                },
            ].filter((step) => !!step.stepName)

            const redeemedSteps = [
                {
                    stepName: OPEN_GIFT_STEP,
                    stepText: 'Open',
                },
                loginOrEmptyStep,
                {
                    stepName: giftDetailsCanBeViewed
                        ? GIFT_DETAILS_STEP
                        : SUCCESS_STEP,
                    stepText: 'Enjoy!',
                },
            ].filter((step) => !!step.stepName)

            const finalizedSteps = !isRedeemed ? unredeemedSteps : redeemedSteps
            const totalPGPlusSteps = finalizedSteps.length

            return {
                finalizedSteps: finalizedSteps,
                currentStepNumber: getCurrentStepNumber(
                    currentStep,
                    totalPGPlusSteps,
                    isLoggedIn,
                    type
                ),
            }
        }
        default:
            return { finalizedSteps: [], currentStepNumber: 1 }
    }
}
